import { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { STATES } from "@/components/leadUtils";
import { CheckCircle2, ArrowRight, ArrowLeft, Home, Building2 } from "lucide-react";

const TIMELINES = ["0-3 months", "3-6 months", "6-12 months", "12+ months"];
const FINANCING = [
  { v: "pre-approved", l: "Pre-approved for a mortgage" },
  { v: "cash", l: "Paying cash" },
  { v: "needs-financing", l: "Need financing" },
  { v: "unknown", l: "Not sure yet" },
];

const empty = {
  role: "", state: "", city: "", zip_code: "", timeline: "",
  budget_min: "", budget_max: "", financing: "unknown", property_type: "Residential",
  name: "", email: "", phone: "", notes: "",
};

export default function LeadCapture() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const progress = (step / 4) * 100;

  const canNext = () => {
    if (step === 1) return !!form.role;
    if (step === 2) return form.state && form.zip_code.length === 5;
    if (step === 3) return form.timeline && form.budget_max;
    return true;
  };

  const submit = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        budget_min: parseInt(form.budget_min || form.budget_max || 0, 10),
        budget_max: parseInt(form.budget_max || form.budget_min || 0, 10),
      };
      await api.post("/leads/capture", payload);
      setDone(true);
      toast.success("Request received! A verified agent will reach out shortly.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#080C14] text-white">
        <Navbar />
        <div className="max-w-lg mx-auto px-4 py-24 text-center fade-up">
          <div className="h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-8 w-8 text-emerald-400" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-3">You're all set!</h1>
          <p className="text-slate-400 mb-8">
            Your request has been received and scored. A verified, exclusive agent in your area
            will reach out shortly to help you {form.role === "seller" ? "sell" : form.role === "both" ? "buy and sell" : "buy"} your home.
          </p>
          <Link to="/">
            <Button className="bg-emerald-400 text-slate-950 hover:bg-emerald-300 font-semibold rounded-full px-8">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080C14] text-white">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-8">
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-400 font-semibold">Free · No obligation</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">
            Buy or sell with a top local agent
          </h1>
        </div>

        <Card
          data-testid="lead-capture-form"
          className="bg-slate-900/90 border border-emerald-500/30 shadow-[0_0_50px_rgba(0,229,153,0.1)] rounded-2xl p-6 sm:p-10 backdrop-blur-2xl"
        >
          <div className="mb-8">
            <div className="flex justify-between text-xs font-mono text-slate-500 mb-2">
              <span>Step {step} of 4</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-1.5 bg-slate-800" />
          </div>

          {step === 1 && (
            <div className="fade-up space-y-4">
              <h2 className="font-heading text-xl font-semibold">What would you like to do?</h2>
              <div className="grid gap-3">
                {[
                  { v: "buyer", l: "Buy a home", Icon: Home },
                  { v: "seller", l: "Sell a home", Icon: Building2 },
                  { v: "both", l: "Both buy & sell", Icon: ArrowRight },
                ].map(({ v, l, Icon }) => (
                  <button
                    key={v}
                    data-testid={`capture-role-${v}`}
                    onClick={() => set("role", v)}
                    className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all ${
                      form.role === v
                        ? "border-emerald-500 bg-emerald-500/10"
                        : "border-slate-800 bg-slate-900 hover:border-slate-700"
                    }`}
                  >
                    <Icon className="h-5 w-5 text-emerald-400" />
                    <span className="font-medium">{l}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="fade-up space-y-4">
              <h2 className="font-heading text-xl font-semibold">Where is the property?</h2>
              <div>
                <Label className="text-slate-400">State</Label>
                <Select value={form.state} onValueChange={(v) => set("state", v)}>
                  <SelectTrigger data-testid="capture-state-select" className="bg-slate-950 border-slate-800 mt-1.5">
                    <SelectValue placeholder="Select a state" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATES.map((s) => (
                      <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-400">City</Label>
                  <Input data-testid="capture-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-slate-950 border-slate-800 mt-1.5" placeholder="e.g. Las Vegas" />
                </div>
                <div>
                  <Label className="text-slate-400">ZIP Code</Label>
                  <Input data-testid="capture-zip-input" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value.replace(/\D/g, "").slice(0, 5))} className="bg-slate-950 border-slate-800 mt-1.5" placeholder="e.g. 89101" />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="fade-up space-y-4">
              <h2 className="font-heading text-xl font-semibold">Timeline & budget</h2>
              <div>
                <Label className="text-slate-400">When are you looking to move?</Label>
                <Select value={form.timeline} onValueChange={(v) => set("timeline", v)}>
                  <SelectTrigger data-testid="capture-timeline-select" className="bg-slate-950 border-slate-800 mt-1.5">
                    <SelectValue placeholder="Select timeline" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMELINES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-400">Budget min ($)</Label>
                  <Input data-testid="capture-budget-min" type="number" value={form.budget_min} onChange={(e) => set("budget_min", e.target.value)} className="bg-slate-950 border-slate-800 mt-1.5" placeholder="500000" />
                </div>
                <div>
                  <Label className="text-slate-400">Budget max ($)</Label>
                  <Input data-testid="capture-budget-max" type="number" value={form.budget_max} onChange={(e) => set("budget_max", e.target.value)} className="bg-slate-950 border-slate-800 mt-1.5" placeholder="800000" />
                </div>
              </div>
              <div>
                <Label className="text-slate-400">Financing</Label>
                <Select value={form.financing} onValueChange={(v) => set("financing", v)}>
                  <SelectTrigger data-testid="capture-financing-select" className="bg-slate-950 border-slate-800 mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FINANCING.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="fade-up space-y-4">
              <h2 className="font-heading text-xl font-semibold">How can an agent reach you?</h2>
              <div>
                <Label className="text-slate-400">Full name</Label>
                <Input data-testid="capture-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-slate-950 border-slate-800 mt-1.5" placeholder="Jane Doe" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-400">Email</Label>
                  <Input data-testid="capture-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-slate-950 border-slate-800 mt-1.5" placeholder="jane@email.com" />
                </div>
                <div>
                  <Label className="text-slate-400">Phone</Label>
                  <Input data-testid="capture-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-slate-950 border-slate-800 mt-1.5" placeholder="3055551234" />
                </div>
              </div>
              <div>
                <Label className="text-slate-400">Anything else? (optional)</Label>
                <Textarea data-testid="capture-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-slate-950 border-slate-800 mt-1.5" placeholder="Tell us about your ideal home…" />
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8">
            {step > 1 ? (
              <Button variant="ghost" onClick={() => setStep((s) => s - 1)} className="text-slate-400 hover:text-white gap-2" data-testid="capture-back-btn">
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            ) : <span />}
            {step < 4 ? (
              <Button
                data-testid="capture-next-btn"
                disabled={!canNext()}
                onClick={() => setStep((s) => s + 1)}
                className="bg-emerald-400 text-slate-950 hover:bg-emerald-300 font-semibold rounded-full px-8 gap-2 disabled:opacity-40"
              >
                Next <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                data-testid="lead-capture-submit-btn"
                onClick={submit}
                disabled={submitting}
                className="bg-emerald-400 text-slate-950 hover:bg-emerald-300 font-semibold rounded-full px-8"
              >
                {submitting ? "Submitting…" : "Get Matched"}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
